/**
 * line-item-save-queue.ts
 *
 * Debounces and serializes line-item writes.
 *
 * The bug this exists to prevent: the estimate builder used to fire one PUT per
 * keystroke with no debounce and no ordering guarantee. Typing "18" into a
 * quantity field sent `quantity=1` and then `quantity=18` as two concurrent
 * requests. If they were applied out of order the row persisted as 1 while the
 * screen still showed 18 from optimistic state — so the generated PDF showed a
 * quantity missing its second digit. Verified on staging: the old code sent 2
 * PUTs for "18", this queue sends 1.
 *
 * Two guarantees:
 *   1. Keystrokes for a row are coalesced into a single write (debounce).
 *   2. Writes for the SAME row never overlap — each is chained behind the last,
 *      so the server applies them in the order the user made them.
 *
 * Different rows still save in parallel; only same-row writes are serialized.
 */

export interface LineItemSaveQueue<T> {
  /** Record a change for `itemId` and (re)start its debounce timer. */
  queue: (itemId: string, patch: T) => void
  /** Drop any pending (not yet sent) change for `itemId` — e.g. the row was deleted. */
  cancel: (itemId: string) => void
  /** Send everything pending and resolve once all in-flight writes have landed. */
  flush: () => Promise<void>
  /** Send `itemId`'s pending change now, without waiting for its timer. */
  saveNow: (itemId: string) => void
  /** Resolves when this row has no write in flight. */
  settled: (itemId: string) => Promise<void>
  /** Pending, not-yet-sent changes. Exposed for last-gasp saves on page unload. */
  pending: () => Array<[string, T]>
}

export function createLineItemSaveQueue<T extends object>(opts: {
  save: (itemId: string, patch: T) => Promise<unknown>
  delayMs: number
}): LineItemSaveQueue<T> {
  const pending = new Map<string, T>()
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const chains = new Map<string, Promise<void>>()

  function clearTimer(itemId: string) {
    const t = timers.get(itemId)
    if (t) clearTimeout(t)
    timers.delete(itemId)
  }

  function saveNow(itemId: string) {
    clearTimer(itemId)
    const patch = pending.get(itemId)
    pending.delete(itemId)
    if (!patch) return

    // Chain behind this row's previous write so the two can't overlap. Swallow
    // errors: the store was already updated optimistically, and a rejected chain
    // would strand every later write for the row.
    const chain = (chains.get(itemId) ?? Promise.resolve())
      .then(() => opts.save(itemId, patch))
      .then(() => {}, () => {})

    chains.set(itemId, chain)
  }

  return {
    queue(itemId, patch) {
      // A later keystroke supersedes an earlier one for the same field.
      pending.set(itemId, { ...(pending.get(itemId) ?? {}), ...patch } as T)
      clearTimer(itemId)
      timers.set(itemId, setTimeout(() => saveNow(itemId), opts.delayMs))
    },

    cancel(itemId) {
      clearTimer(itemId)
      pending.delete(itemId)
    },

    saveNow,

    async flush() {
      for (const itemId of [...pending.keys()]) saveNow(itemId)
      await Promise.all([...chains.values()])
    },

    async settled(itemId) {
      await chains.get(itemId)
      chains.delete(itemId)
    },

    pending: () => [...pending.entries()],
  }
}
